import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatPrice, resolvePhoto } from "@/lib/api";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { useT } from "@/i18n/I18nContext";
import { StarRating } from "@/components/StarRating";
import { formatDateTimeFr } from "@/components/DateFieldFr";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ORDER_ICONS } from "@/lib/orderIcons";
import {
  ArrowLeft, ShieldCheck, Smartphone, Upload, Loader2, Truck, CheckCircle2, XCircle, Lock,
  AlertTriangle, Send, Gavel, Clock, PackageCheck, Copy, MapPin, Camera, ImagePlus, FileText, Download,
} from "lucide-react";

const BigAction = ({ icon, label, onClick, disabled, tone = "border-primary/40 bg-primary/5 hover:bg-primary/10", textCls = "text-secondary", testid }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
    className={`w-full flex items-center gap-4 rounded-2xl border-2 p-4 text-left transition-colors active:scale-[0.98] disabled:opacity-60 ${tone}`}>
    <img src={icon} alt="" className="h-16 w-16 rounded-xl object-cover shrink-0 pointer-events-none" />
    <span className={`font-heading font-bold text-lg leading-tight ${textCls}`}>{label}</span>
  </button>
);

const TRACK_STEPS = (t) => [
  { key: "prep", icon: ORDER_ICONS.prep, label: t("order.stepPrepShort") },
  { key: "moto", icon: ORDER_ICONS.moto, label: t("order.stepMotoShort") },
  { key: "delivered", icon: ORDER_ICONS.delivered, label: t("order.stepDeliveredShort") },
  { key: "conforme", icon: ORDER_ICONS.conforme, label: t("order.stepConformeShort") },
];

const trackIndex = (status) => {
  if (["paid", "preparing"].includes(status)) return 0;
  if (status === "shipped") return 1;
  if (status === "handed_over") return 2;
  if (["delivered", "closed"].includes(status)) return 3;
  return -1;
};

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const brand = useBrand();
  const { t } = useT();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [method, setMethod] = useState("manual_transfer");
  const [proof, setProof] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [dispute, setDispute] = useState(null);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [dmsg, setDmsg] = useState("");
  const [dmsgPhotos, setDmsgPhotos] = useState([]);
  const [returnOpen, setReturnOpen] = useState(false);
  const [motif, setMotif] = useState("");
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receivePhase, setReceivePhase] = useState("warn");
  const [problemPhotos, setProblemPhotos] = useState([]);
  const [feeVal, setFeeVal] = useState("");
  const [refuseOpen, setRefuseOpen] = useState(false);
  const [refuseMotif, setRefuseMotif] = useState("Distance");

  const acceptOrder = async () => {
    setBusy(true);
    try { await api.post(`/orders/${id}/accept`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const doRefuse = async () => {
    setBusy(true);
    try { await api.post(`/orders/${id}/refuse`, { motif: refuseMotif }); setRefuseOpen(false); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const setDeliveryFee = async () => {
    setBusy(true);
    try { await api.put(`/orders/${id}/delivery-fee`, { fee: Number(feeVal) || 0 }); setFeeVal(""); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const load = () => Promise.all([api.get(`/orders/${id}`), api.get(`/disputes/by_order/${id}`)])
    .then(([o, d]) => { setOrder(o.data); setDispute(d.data); })
    .finally(() => setLoading(false));
  useEffect(() => { window.scrollTo(0, 0); load(); }, [id]);
  useAutoRefresh(load, 12000);

  const isBuyer = order && user.id === order.buyer_id;

  const changeStatus = async (status) => {
    setBusy(true);
    try { await api.patch(`/orders/${id}/status`, { status }); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const uploadProof = async (file) => {
    if (!file) return;
    const name = (file.name || "").toLowerCase();
    const ok = /\.(jpe?g|png|pdf)$/.test(name) || ["image/jpeg", "image/png", "application/pdf"].includes(file.type);
    if (!ok) { toast.error(t("order.proofBadFormat")); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProof(data.path);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("order.proofBadFormat"));
    }
  };

  const proofIsPdf = typeof proof === "string" && proof.toLowerCase().endsWith(".pdf");

  const downloadReceipt = async () => {
    try {
      const res = await api.get(`/orders/${id}/receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `recu-ovolaay-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t("order.receiptError"));
    }
  };

  const pay = async () => {
    if (!proof) { toast.error(t("order.proofRequired")); return; }
    setBusy(true);
    try {
      await api.post(`/orders/${id}/pay`, { method: "manual_transfer", proof });
      setPayOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const validateReception = async (conforme, motifText) => {
    setBusy(true);
    try {
      await api.post(`/orders/${id}/validate`, { conforme, motif: conforme ? "" : ((motifText || motif).trim() || "Problème signalé à la réception"), photos: conforme ? [] : problemPhotos });
      setReturnOpen(false); setReceiveOpen(false); setReceivePhase("warn"); setMotif(""); setProblemPhotos([]); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const uploadProblemPhoto = async (file) => {
    if (!file) return;
    const name = (file.name || "").toLowerCase();
    const ok = /\.(jpe?g|png)$/.test(name) || ["image/jpeg", "image/png"].includes(file.type);
    if (!ok) { toast.error(t("order.proofBadFormat")); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProblemPhotos((p) => [...p, data.path]);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const submitReview = async () => {
    if (!rating) { toast.error(t("review.yourRating")); return; }
    setBusy(true);
    try {
      await api.post("/reviews", { order_id: id, rating, comment });
      toast.success(t("review.thanks")); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const openDispute = async () => {
    if (!reason.trim()) { toast.error(t("dispute.reason")); return; }
    setBusy(true);
    try { await api.post(`/orders/${id}/dispute`, { reason }); setDisputeOpen(false); setReason(""); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const sendDMsg = async () => {
    if (!dmsg.trim() && dmsgPhotos.length === 0) return;
    await api.post(`/disputes/${dispute.id}/messages`, { body: dmsg, images: dmsgPhotos });
    setDmsg(""); setDmsgPhotos([]); await load();
  };
  const uploadDisputePhoto = async (file) => {
    if (!file) return;
    const name = (file.name || "").toLowerCase();
    const ok = /\.(jpe?g|png)$/.test(name) || ["image/jpeg", "image/png"].includes(file.type);
    if (!ok) { toast.error(t("order.proofBadFormat")); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDmsgPhotos((p) => [...p, data.path]);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const escalate = async () => { await api.post(`/disputes/${dispute.id}/escalate`); await load(); };

  if (loading) return <div className="container-page py-10"><Skeleton className="h-64 rounded-2xl" /></div>;
  if (!order) return null;

  const guaranteeText = order.payment_status === "released"
    ? t("order.guaranteeReleased")
    : ["paid", "shipped"].includes(order.status) ? t("order.guaranteeHeld") : null;

  return (
    <div className="container-page py-10 max-w-3xl">
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 -ml-2 text-muted-foreground hover:text-primary" data-testid="order-back">
        <ArrowLeft className="h-4 w-4 mr-1.5" /> {t("common.back")}
      </Button>

      <div className="rounded-2xl bg-card border border-border p-6">
        <div className="flex items-start gap-4">
          <img src={resolvePhoto(order.listing_image)} alt="" className="h-24 w-24 rounded-xl object-cover" />
          <div className="flex-1">
            <h1 className="font-heading font-extrabold text-2xl text-secondary">{order.listing_title}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isBuyer ? `${t("order.seller")}: ` : `${t("order.buyer")}: `}
              {isBuyer
                ? <Link to={`/profil-vendeur/${order.seller_id}`} className="text-primary hover:underline">{order.seller_name}</Link>
                : order.buyer_name}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge className="bg-primary/10 text-primary border-0" data-testid="order-status">{t(`order.statuses.${order.status}`)}</Badge>
              <Badge className="bg-muted text-muted-foreground border-0">{t(`order.paymentStatuses.${order.payment_status}`)}</Badge>
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-3 gap-3 text-center">
          <div className="rounded-xl border border-border p-3"><p className="text-xs text-muted-foreground">{t("order.quantity")}</p><p className="font-semibold text-secondary">{order.quantity}</p></div>
          <div className="rounded-xl border border-border p-3"><p className="text-xs text-muted-foreground">{t("order.pay")}</p><p className="font-semibold text-secondary">{formatPrice(order.unit_price, brand.currencySymbol)}</p></div>
          <div className="rounded-xl border border-border p-3 bg-primary/5"><p className="text-xs text-muted-foreground">{t("order.total")}</p><p className="font-heading font-bold text-primary">{formatPrice(order.total_amount, brand.currencySymbol)}</p></div>
        </div>

        {order.protection_amount != null && (
          <div className="mt-4 rounded-2xl border border-border p-4" data-testid="order-protection">
            <div className="flex items-center justify-between py-1 text-sm">
              <span className="text-muted-foreground">{t("protection.amount")}</span>
              <span className="font-medium text-foreground">{formatPrice(order.total_amount, brand.currencySymbol)}</span>
            </div>
            <div className="flex items-center justify-between py-1 text-sm">
              <span className="text-muted-foreground flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-success" /> {t("protection.fee")}</span>
              <span className="font-medium text-foreground">{formatPrice(order.protection_amount, brand.currencySymbol)}</span>
            </div>
            {order.delivery_fee > 0 && (
              <div className="flex items-center justify-between py-1 text-sm">
                <span className="text-muted-foreground flex items-center gap-1.5"><Truck className="h-4 w-4 text-primary" /> {t("order.deliveryFee")}</span>
                <span className="font-medium text-foreground">{formatPrice(order.delivery_fee, brand.currencySymbol)}</span>
              </div>
            )}
            <div className="flex items-center justify-between py-1.5 mt-1 border-t border-border text-base">
              <span className="font-semibold text-foreground">{t("protection.total")}</span>
              <span className="font-heading font-bold text-primary">{formatPrice(order.grand_total ?? order.total_amount, brand.currencySymbol)}</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">{t("protection.note")}</p>
          </div>
        )}

        {order.status === "pending_fee" && (
          <div className="mt-4 rounded-xl bg-accent/40 border border-border p-4 flex items-center gap-2 text-sm text-foreground" data-testid="pending-fee-box">
            <Clock className="h-5 w-5 text-primary shrink-0" /> {t("order.deliveryPending")}
          </div>
        )}
        {order.status === "awaiting_validation" && (
          <div className="mt-4 rounded-xl bg-accent/40 border border-border p-4 flex items-center gap-2 text-sm text-foreground" data-testid="awaiting-validation-box">
            <ShieldCheck className="h-5 w-5 text-primary shrink-0" /> {t("order.awaitingValidationNote")}
          </div>
        )}

        {/* Buyer contact reveal for seller after payment validation */}
        {!isBuyer && order.buyer_phone && order.phone_unlocked && (
          <div className="mt-4 rounded-2xl border border-success/40 bg-success/5 p-4" data-testid="buyer-contact-box">
            <p className="font-heading font-semibold text-secondary flex items-center gap-2"><Smartphone className="h-4 w-4 text-success" /> {t("order.buyerContact")}</p>
            <a href={`tel:${order.buyer_phone}`} className="mt-1 block text-lg font-bold text-success" data-testid="buyer-phone">{order.buyer_phone}</a>
            {order.delivery_address && (
              <p className="mt-2 text-sm text-muted-foreground" data-testid="delivery-address">
                {t("order.deliveryAddress")} : {order.delivery_address.full_address || [order.delivery_address.neighborhood, order.delivery_address.commune, order.delivery_address.region].filter(Boolean).join(", ")}
                {order.delivery_address.note ? ` — ${order.delivery_address.note}` : ""}
              </p>
            )}
            <p className="mt-2 text-xs text-muted-foreground">{t("order.sellerOrganizes")}</p>
          </div>
        )}
        {!isBuyer && !order.phone_unlocked && ["pending_fee", "pending_payment", "awaiting_validation"].includes(order.status) && (
          <div className="mt-4 rounded-xl border border-dashed border-border p-4 flex items-center gap-2 text-sm text-muted-foreground" data-testid="contact-locked-box">
            <Lock className="h-4 w-4" /> {t("order.contactLocked")}
          </div>
        )}

        {guaranteeText && (
          <div className="mt-4 rounded-xl bg-secondary/5 border border-secondary/20 p-4 flex items-center gap-2 text-sm text-secondary" data-testid="guarantee-box">
            <ShieldCheck className="h-5 w-5" /> <span className="font-semibold">{t("order.guarantee")} :</span> {guaranteeText}
          </div>
        )}

        {order.delivery_address?.full_address && (
          <div className="mt-4 rounded-2xl border border-border bg-card p-4" data-testid="order-delivery-address">
            <p className="font-heading font-semibold text-secondary flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> {t("order.deliveryAddress")}</p>
            <p className="mt-1 text-sm text-foreground whitespace-pre-line" data-testid="order-delivery-address-value">{order.delivery_address.full_address}</p>
            {order.delivery_address.note && (
              <p className="mt-1 text-xs text-muted-foreground">{t("order.deliveryNote")} : {order.delivery_address.note}</p>
            )}
            {!isBuyer && order.status === "pending_fee" && (
              <p className="mt-2 text-xs text-primary font-medium">{t("order.addressForFee")}</p>
            )}
          </div>
        )}

        {isBuyer && order.payment_status === "paid" && (
          <Button onClick={downloadReceipt} variant="outline" className="mt-4 rounded-full border-primary/40 text-primary hover:bg-primary/5" data-testid="download-receipt-btn">
            <Download className="h-4 w-4 mr-2" /> {t("order.downloadReceipt")}
          </Button>
        )}

        {/* Visual order tracking (icons + short words) */}
        {trackIndex(order.status) >= 0 && (
          <div className="mt-6" data-testid="order-track">
            <h3 className="font-heading font-semibold text-secondary mb-3">{t("order.trackTitle")}</h3>
            <div className="flex items-stretch justify-between gap-1">
              {TRACK_STEPS(t).map((s, i) => {
                const idx = trackIndex(order.status);
                const done = i < idx;
                const active = i === idx;
                return (
                  <div key={s.key} className="flex flex-col items-center flex-1 min-w-0" data-testid={`track-step-${s.key}`}>
                    <div className={`relative h-14 w-14 rounded-2xl overflow-hidden border-2 transition-all ${active ? "border-primary ring-2 ring-primary/30 scale-105" : done ? "border-success" : "border-border opacity-45 grayscale"}`}>
                      <img src={s.icon} alt="" className="h-full w-full object-cover pointer-events-none" />
                      {done && <span className="absolute inset-0 bg-success/25 flex items-center justify-center"><CheckCircle2 className="h-5 w-5 text-white drop-shadow" /></span>}
                    </div>
                    <span className={`mt-1 text-[11px] font-semibold text-center leading-tight ${active ? "text-primary" : done ? "text-success" : "text-muted-foreground"}`}>{s.label}</span>
                    {i < TRACK_STEPS(t).length - 1 && <span className="sr-only">→</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-5 flex flex-wrap gap-3">
          {!isBuyer && order.status === "pending_seller" && (
            <>
              <Button onClick={acceptOrder} disabled={busy} className="rounded-full bg-success hover:bg-success/90 text-white" data-testid="accept-order-btn"><CheckCircle2 className="h-4 w-4 mr-1.5" />{t("order.acceptOrder")}</Button>
              <Button variant="outline" onClick={() => setRefuseOpen(true)} disabled={busy} className="rounded-full text-destructive border-destructive/40" data-testid="refuse-order-btn"><XCircle className="h-4 w-4 mr-1.5" />{t("order.refuseOrder")}</Button>
            </>
          )}
          {isBuyer && order.status === "pending_seller" && (
            <div className="w-full rounded-xl bg-accent/40 border border-border p-4 flex items-center gap-2 text-sm text-foreground" data-testid="awaiting-seller-box">
              <Clock className="h-5 w-5 text-primary shrink-0" /> {t("order.awaitingSellerNote")}
            </div>
          )}
          {isBuyer && order.status === "refused" && (
            <Link to={`/catalogue?category=${order.category}`} className="w-full" data-testid="see-other-sellers">
              <Button className="w-full rounded-full bg-primary hover:bg-primary/90"><ArrowLeft className="h-4 w-4 mr-1.5 rotate-180" />{t("order.seeOtherSellers")}</Button>
            </Link>
          )}
          {isBuyer && order.status === "pending_fee" && (
            <Button variant="outline" onClick={() => changeStatus("cancelled")} disabled={busy} className="rounded-full text-destructive" data-testid="cancel-order-btn"><XCircle className="h-4 w-4 mr-1.5" />{t("order.cancel")}</Button>
          )}
          {!isBuyer && order.status === "pending_fee" && (
            <div className="w-full flex flex-wrap items-center gap-2" data-testid="seller-fee-row">
              <span className="text-sm font-semibold text-secondary">{t("order.deliveryFee")} :</span>
              <input type="number" min={0} value={feeVal} onChange={(e) => setFeeVal(e.target.value)} placeholder="FCFA" className="h-11 w-36 rounded-lg border border-border px-3 text-sm" data-testid="seller-fee-input" />
              <Button onClick={setDeliveryFee} disabled={busy} className="rounded-full bg-secondary hover:bg-secondary/90" data-testid="seller-fee-save"><Truck className="h-4 w-4 mr-1.5" />{t("admin.setFeeBtn")}</Button>
            </div>
          )}
          {isBuyer && order.status === "pending_payment" && (
            <>
              <Button onClick={() => setPayOpen(true)} className="rounded-full bg-primary hover:bg-primary/90" data-testid="pay-btn">{t("order.pay")}</Button>
              <Button variant="outline" onClick={() => changeStatus("cancelled")} disabled={busy} className="rounded-full text-destructive" data-testid="cancel-order-btn"><XCircle className="h-4 w-4 mr-1.5" />{t("order.cancel")}</Button>
            </>
          )}
          {!isBuyer && order.status === "paid" && (
            <BigAction icon={ORDER_ICONS.prep} label={t("order.sellerPrepareBtn")} onClick={() => changeStatus("preparing")} disabled={busy}
              tone="border-secondary/40 bg-secondary/5 hover:bg-secondary/10" testid="prepare-btn" />
          )}
          {!isBuyer && order.status === "preparing" && (
            <BigAction icon={ORDER_ICONS.moto} label={t("order.sellerShipBtn")} onClick={() => changeStatus("shipped")} disabled={busy}
              tone="border-primary/40 bg-primary/5 hover:bg-primary/10" testid="ship-btn" />
          )}
          {!isBuyer && order.status === "shipped" && (
            <BigAction icon={ORDER_ICONS.delivered} label={t("order.sellerDeliveredBtn")} onClick={() => changeStatus("handed_over")} disabled={busy}
              tone="border-success/40 bg-success/5 hover:bg-success/10" textCls="text-success" testid="handover-btn" />
          )}
          {!isBuyer && order.status === "handed_over" && (
            <div className="w-full rounded-xl bg-accent/40 border border-border p-4 flex items-center gap-2 text-sm text-foreground" data-testid="seller-wait-buyer-box">
              <Clock className="h-5 w-5 text-primary shrink-0" /> {t("order.sellerWaitBuyer")}
            </div>
          )}
          {isBuyer && ["shipped", "handed_over"].includes(order.status) && (
            <BigAction icon={ORDER_ICONS.received} label={t("order.buyerReceivedBtn")} onClick={() => { setReceivePhase("warn"); setReceiveOpen(true); }} disabled={busy}
              tone="border-success/50 bg-success/5 hover:bg-success/10" textCls="text-success" testid="buyer-received-btn" />
          )}
          {!dispute && ["paid", "shipped", "handed_over"].includes(order.status) && (
            <Button variant="outline" onClick={() => setDisputeOpen(true)} className="rounded-full text-destructive border-destructive/40" data-testid="open-dispute-btn">
              <AlertTriangle className="h-4 w-4 mr-1.5" />{t("dispute.open")}
            </Button>
          )}
        </div>

        {dispute && (
          <div className="mt-6 rounded-xl border border-destructive/30 bg-destructive/5 p-4" data-testid="dispute-box">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-heading font-semibold text-secondary flex items-center gap-2"><Gavel className="h-4 w-4" /> {t("dispute.title")}</h3>
              <Badge className="bg-card border border-border text-secondary" data-testid="dispute-status">
                {dispute.status === "open" ? t("dispute.openState")
                  : dispute.status === "escalated" ? t("dispute.escalated")
                  : dispute.status === "resolved_refund" ? t("dispute.resolvedRefund")
                  : t("dispute.resolvedRelease")}
              </Badge>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground" data-testid="dispute-dates">
              {dispute.created_at && <span>{t("dispute.openedAt")} : {formatDateTimeFr(dispute.created_at)}</span>}
              {dispute.resolved_at && <span>{t("dispute.resolvedAt")} : {formatDateTimeFr(dispute.resolved_at)}</span>}
            </div>
            <div className="mt-3 space-y-2 max-h-56 overflow-y-auto" data-testid="dispute-thread">
              {dispute.messages?.map((m, i) => (
                <div key={i} className={`text-sm rounded-lg px-3 py-2 ${m.sender_role === "admin" ? "bg-secondary text-secondary-foreground" : m.sender_role === "system" ? "bg-muted text-muted-foreground italic" : m.sender_id === user.id ? "bg-primary/10" : "bg-card border border-border"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] uppercase font-semibold opacity-70">{m.sender_role}</span>
                    {m.at && <span className="text-[10px] opacity-60">{formatDateTimeFr(m.at)}</span>}
                  </div>
                  {m.body && <p>{m.body}</p>}
                  {m.images?.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-2" data-testid={`dispute-msg-images-${i}`}>
                      {m.images.map((img, j) => (
                        <a key={j} href={resolvePhoto(img)} target="_blank" rel="noreferrer" className="block h-20 w-20 rounded-lg overflow-hidden border border-border">
                          <img src={resolvePhoto(img)} alt="" className="h-full w-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {!dispute.status.startsWith("resolved") && (
              <div className="mt-3">
                {dmsgPhotos.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2" data-testid="dispute-compose-photos">
                    {dmsgPhotos.map((p, i) => (
                      <div key={i} className="relative h-16 w-16 rounded-lg overflow-hidden border border-border">
                        <img src={resolvePhoto(p)} alt="" className="h-full w-full object-cover" />
                        <button type="button" onClick={() => setDmsgPhotos((arr) => arr.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5" data-testid={`dispute-compose-photo-remove-${i}`}>
                          <XCircle className="h-3.5 w-3.5 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <label htmlFor="dispute-photo-input" className="flex items-center justify-center h-10 w-11 rounded-md border border-border cursor-pointer hover:bg-muted transition-colors shrink-0" data-testid="dispute-photo-label" title={t("order.addProblemPhoto")}>
                    <Camera className="h-4 w-4 text-primary" />
                  </label>
                  <input id="dispute-photo-input" type="file" accept="image/jpeg,image/png" className="hidden" data-testid="dispute-photo-input"
                    onChange={(e) => { uploadDisputePhoto(e.target.files?.[0]); e.target.value = ""; }} />
                  <Input value={dmsg} onChange={(e) => setDmsg(e.target.value)} placeholder={t("dispute.send")} data-testid="dispute-msg-input" />
                  <Button onClick={sendDMsg} className="bg-primary hover:bg-primary/90" data-testid="dispute-msg-send"><Send className="h-4 w-4" /></Button>
                </div>
              </div>
            )}
            {dispute.status === "open" && (
              <Button variant="outline" onClick={escalate} className="mt-3 rounded-full text-secondary border-secondary/40" data-testid="escalate-btn">
                <Gavel className="h-4 w-4 mr-1.5" />{t("dispute.escalate")}
              </Button>
            )}
          </div>
        )}

        {/* Timeline */}
        <div className="mt-6">
          <h3 className="font-heading font-semibold text-secondary mb-3">{t("order.timeline")}</h3>
          <ol className="relative border-l border-border ml-2" data-testid="order-timeline">
            {order.timeline?.map((ev, i) => (
              <li key={i} className="ml-4 pb-4">
                <span className="absolute -left-1.5 h-3 w-3 rounded-full bg-primary pointer-events-none" />
                <p className="text-sm text-secondary font-medium">{ev.label}</p>
                <p className="text-xs text-muted-foreground">{new Date(ev.at).toLocaleString("fr-FR")}</p>
              </li>
            ))}
          </ol>
        </div>

        {/* Review */}
        {isBuyer && order.status === "delivered" && !order.reviewed && (
          <div className="mt-6 rounded-xl border border-border p-4" data-testid="review-form">
            <h3 className="font-heading font-semibold text-secondary">{t("review.leaveReview")}</h3>
            <p className="text-xs text-muted-foreground mt-1">{t("review.verified")}</p>
            <div className="mt-3"><StarRating value={rating} size={26} onChange={setRating} /></div>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("review.comment")} className="mt-3" rows={3} data-testid="review-comment" />
            <Button onClick={submitReview} disabled={busy} className="mt-3 rounded-full bg-primary hover:bg-primary/90" data-testid="review-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{t("review.submit")}
            </Button>
          </div>
        )}
        {order.reviewed && (
          <div className="mt-6 rounded-xl bg-secondary/5 border border-secondary/20 p-4 text-sm text-secondary flex items-center gap-2" data-testid="reviewed-box">
            <CheckCircle2 className="h-4 w-4" /> {t("review.thanks")}
          </div>
        )}
      </div>

      {/* Payment dialog — manual receipt upload */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="pay-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-secondary">{t("order.payTitle")}</DialogTitle>
            <DialogDescription>{t("order.payReceiptNote")}</DialogDescription>
          </DialogHeader>
          {brand.orangeMoney && (
            <div className="rounded-xl border-2 border-[#ff7900]/40 bg-[#ff7900]/5 p-4" data-testid="orange-money-block">
              <p className="font-bold text-[#ff7900] flex items-center gap-2"><Smartphone className="h-5 w-5" /> {t("order.payVia")}</p>
              <div className="mt-2 flex items-center justify-between rounded-lg bg-white border border-border px-3 py-2">
                <div>
                  <p className="text-xs text-muted-foreground">{t("order.omNumber")}</p>
                  <p className="font-heading font-extrabold text-lg text-secondary tracking-wide" data-testid="om-number">{brand.orangeMoney}</p>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(brand.orangeMoney.replace(/\s/g, "")); toast.success(t("order.copied")); }} data-testid="om-copy">
                  <Copy className="h-4 w-4 mr-1.5" /> {t("order.copy")}
                </Button>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t("order.amountToSend")}</span>
                <span className="font-heading font-bold text-[#ff7900]" data-testid="om-amount">{formatPrice(order.grand_total ?? order.total_amount, brand.currencySymbol)}</span>
              </div>
            </div>
          )}
          <div className="mt-1 grid grid-cols-2 gap-2">
            <label className="flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-border p-4 cursor-pointer hover:border-primary transition-colors" data-testid="proof-camera-label">
              <Camera className="h-6 w-6 text-muted-foreground" />
              <span className="text-xs font-semibold text-secondary text-center">{t("order.takePhoto")}</span>
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files && uploadProof(e.target.files[0])} data-testid="proof-input" />
            </label>
            <label className="flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-border p-4 cursor-pointer hover:border-primary transition-colors" data-testid="proof-gallery-label">
              <ImagePlus className="h-6 w-6 text-muted-foreground" />
              <span className="text-xs font-semibold text-secondary text-center">{t("order.fromGallery")}</span>
              <input type="file" accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf" className="hidden" onChange={(e) => e.target.files && uploadProof(e.target.files[0])} data-testid="proof-gallery-input" />
            </label>
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground text-center">{t("order.proofFormats")}</p>
          {proof && (
            <div className="mt-2 flex items-center gap-3 rounded-xl bg-success/5 border border-success/30 p-3" data-testid="proof-preview">
              {proofIsPdf
                ? <FileText className="h-10 w-10 text-primary shrink-0" />
                : <img src={resolvePhoto(proof)} alt="" className="h-14 w-14 rounded object-cover" />}
              <span className="text-sm font-semibold text-success flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> {t("order.receiptAdded")}</span>
            </div>
          )}
          <div className="flex items-center justify-between rounded-xl bg-muted/50 px-4 py-3 mt-2">
            <span className="text-sm text-muted-foreground">{t("protection.total")}</span>
            <span className="font-heading font-bold text-xl text-primary">{formatPrice(order.grand_total ?? order.total_amount, brand.currencySymbol)}</span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={pay} disabled={busy || !proof} className="bg-primary hover:bg-primary/90" data-testid="pay-confirm">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}<Lock className="h-4 w-4 mr-1.5" />{t("order.paidBtn")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refuse order dialog (seller) */}
      <Dialog open={refuseOpen} onOpenChange={setRefuseOpen}>
        <DialogContent data-testid="refuse-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-secondary">{t("order.refuseTitle")}</DialogTitle>
            <DialogDescription>{order.listing_title}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            {[["Distance", t("order.motifDistance")], ["Quantité", t("order.motifQuantite")], ["Rupture", t("order.motifRupture")], ["Autre", t("order.motifAutre")]].map(([val, label]) => (
              <button key={val} type="button" onClick={() => setRefuseMotif(val)} data-testid={`motif-${val}`}
                className={`rounded-xl border-2 p-3 text-sm font-semibold transition-colors ${refuseMotif === val ? "border-destructive bg-destructive/5 text-destructive" : "border-border text-muted-foreground"}`}>
                {label}
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefuseOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={doRefuse} disabled={busy} className="bg-destructive hover:bg-destructive/90" data-testid="refuse-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{t("order.refuseOrder")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Non-conforme return dialog */}
      <Dialog open={returnOpen} onOpenChange={setReturnOpen}>
        <DialogContent data-testid="return-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-secondary">{t("order.nonConformeTitle")}</DialogTitle>
            <DialogDescription>{order.listing_title}</DialogDescription>
          </DialogHeader>
          <Textarea value={motif} onChange={(e) => setMotif(e.target.value)} placeholder={t("order.motifPlaceholder")} rows={4} data-testid="return-motif" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={() => validateReception(false)} disabled={busy} className="bg-destructive hover:bg-destructive/90" data-testid="return-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{t("order.nonConforme")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
        <DialogContent data-testid="dispute-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-secondary">{t("dispute.openTitle")}</DialogTitle>
            <DialogDescription>{order.listing_title}</DialogDescription>
          </DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("dispute.reasonPlaceholder")} rows={4} data-testid="dispute-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisputeOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={openDispute} disabled={busy} className="bg-destructive hover:bg-destructive/90" data-testid="dispute-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{t("dispute.submit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Buyer reception flow: warning -> choice (conforme / problème) */}
      <Dialog open={receiveOpen} onOpenChange={(v) => { if (!v) { setReceiveOpen(false); setReceivePhase("warn"); } }}>
        <DialogContent data-testid="receive-dialog">
          {receivePhase === "warn" && (
            <>
              <div className="flex flex-col items-center text-center pt-2">
                <img src={ORDER_ICONS.warningCheck} alt="" className="h-28 w-28 rounded-2xl object-cover" />
                <DialogTitle className="font-heading text-secondary text-xl mt-3">{t("order.warnTitle")}</DialogTitle>
                <p className="text-sm text-muted-foreground mt-2">{t("order.warnBody")}</p>
              </div>
              <DialogFooter className="mt-2">
                <Button variant="outline" onClick={() => { setReceiveOpen(false); setReceivePhase("warn"); }} data-testid="receive-cancel">{t("order.warnCancel")}</Button>
                <Button onClick={() => setReceivePhase("choose")} className="bg-primary hover:bg-primary/90" data-testid="receive-continue">{t("order.warnContinue")}</Button>
              </DialogFooter>
            </>
          )}
          {receivePhase === "choose" && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-secondary text-center">{t("order.chooseTitle")}</DialogTitle>
                <DialogDescription className="text-center">{order.listing_title}</DialogDescription>
              </DialogHeader>
              <div className="grid gap-3 mt-1">
                <BigAction icon={ORDER_ICONS.conforme} label={t("order.confirmConformeBtn")} onClick={() => validateReception(true)} disabled={busy}
                  tone="border-success/50 bg-success/5 hover:bg-success/10" textCls="text-success" testid="confirm-conforme-btn" />
                <BigAction icon={ORDER_ICONS.problem} label={t("order.confirmProblemBtn")} onClick={() => setReceivePhase("problem")} disabled={busy}
                  tone="border-destructive/50 bg-destructive/5 hover:bg-destructive/10" textCls="text-destructive" testid="confirm-problem-btn" />
              </div>
            </>
          )}
          {receivePhase === "problem" && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-destructive">{t("order.nonConformeTitle")}</DialogTitle>
                <DialogDescription>{order.listing_title}</DialogDescription>
              </DialogHeader>
              <Textarea value={motif} onChange={(e) => setMotif(e.target.value)} placeholder={t("order.motifPlaceholder")} rows={4} data-testid="problem-motif" />
              <div className="mt-1">
                <label htmlFor="problem-photo-input" className="inline-flex items-center gap-2 cursor-pointer rounded-xl border-2 border-dashed border-border px-4 py-3 text-sm font-semibold text-secondary hover:bg-muted transition-colors" data-testid="problem-photo-label">
                  <Camera className="h-5 w-5 text-primary" /> {t("order.addProblemPhoto")}
                </label>
                <input id="problem-photo-input" type="file" accept="image/jpeg,image/png" className="hidden" data-testid="problem-photo-input"
                  onChange={(e) => { uploadProblemPhoto(e.target.files?.[0]); e.target.value = ""; }} />
                <p className="text-xs text-muted-foreground mt-1">{t("order.problemPhotoHint")}</p>
                {problemPhotos.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="problem-photos">
                    {problemPhotos.map((p, i) => (
                      <div key={i} className="relative h-20 w-20 rounded-lg overflow-hidden border border-border" data-testid={`problem-photo-${i}`}>
                        <img src={resolvePhoto(p)} alt="" className="h-full w-full object-cover" />
                        <button type="button" onClick={() => setProblemPhotos((arr) => arr.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5" data-testid={`problem-photo-remove-${i}`}>
                          <XCircle className="h-4 w-4 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setReceivePhase("choose")}>{t("common.back")}</Button>
                <Button onClick={() => validateReception(false)} disabled={busy} className="bg-destructive hover:bg-destructive/90" data-testid="problem-submit">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{t("dispute.open")}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
